/**
 * Combobox: a text input that filters a list of options and lets one be
 * chosen, for a form field.
 *
 * The same APG "combobox with listbox popup" the command palette implements —
 * real focus stays in the input, a virtual cursor moves with
 * `aria-activedescendant`, options are `role="option"` out of the tab order —
 * in a form control rather than a dialog. The options and their loading,
 * empty and error states are the caller's: this component only knows how to
 * show a list, move through it and pick from it, so that one keyboard model
 * serves every place a field needs a choice from a list the server returns.
 *
 * It is controlled in both halves: the typed text (`query`) and the chosen
 * value (`value`). Choosing an option sets the value and closes the list;
 * clearing sets the value to null. The input shows the chosen label while
 * nothing is being typed, so a field that has a value reads like a select.
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { cx } from '~/lib/cx';

import styles from './Combobox.module.css';
import { useField } from './Form';
import { ChevronUpDown, Close } from './icons';

export interface ComboboxOption {
  /** What is submitted and compared; unique in the list. */
  value: string;
  /** What is shown; the value when absent. */
  label?: string;
  /** Shown after the label — a badge, a hint. */
  trailing?: ReactNode;
}

export interface ComboboxProps {
  /** The options to show, already filtered by the caller. */
  options: ComboboxOption[];
  /** The chosen value, or null for none. */
  value: string | null;
  onValueChange: (value: string | null) => void;
  /** The typed text. */
  query: string;
  onQueryChange: (query: string) => void;
  /** Shown while nothing is chosen and nothing is typed. */
  placeholder?: string;
  /**
   * Replaces the list: a loading, empty or error state the caller renders.
   * When set, the options are not shown.
   */
  status?: ReactNode;
  /** Shown under the options — "showing the first 100", for instance. */
  footer?: ReactNode;
  disabled?: boolean;
  id?: string;
  /** Required when the combobox is not inside a `Field`. */
  'aria-label'?: string;
  className?: string;
}

export function Combobox({
  options,
  value,
  onValueChange,
  query,
  onQueryChange,
  placeholder,
  status,
  footer,
  disabled,
  id,
  'aria-label': ariaLabel,
  className,
}: ComboboxProps) {
  const field = useField(id);
  const controlId = id ?? field.controlId;
  const listId = `${useId()}-listbox`;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const chosen = value === null ? undefined : options.find((o) => o.value === value);
  // While the list is open the input is a search box; closed, it shows the
  // choice. The caller's `query` is the search text and nothing else.
  const shown = open ? query : (chosen?.label ?? value ?? '');
  const showing = status ? [] : options;
  // The cursor is clamped to the list rather than reset by an effect: a
  // list that shrank under the cursor shows its last row, not a blank.
  const cursor = showing.length === 0 ? 0 : Math.min(activeIndex, showing.length - 1);
  const activeId = open && showing.length > 0 ? `${listId}-${cursor}` : undefined;

  useEffect(() => {
    if (!open || !activeId) return;
    document.getElementById(activeId)?.scrollIntoView({ block: 'nearest' });
  }, [open, activeId]);

  // Close when focus or a click leaves the control, as a native select does.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const choose = (option: ComboboxOption) => {
    onValueChange(option.value);
    onQueryChange('');
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) {
          setOpen(true);
          setActiveIndex(0);
        } else if (showing.length > 0) {
          setActiveIndex((cursor + 1) % showing.length);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (open && showing.length > 0) {
          setActiveIndex((cursor - 1 + showing.length) % showing.length);
        }
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(Math.max(0, showing.length - 1));
        }
        break;
      case 'Enter':
        if (open) {
          // The form must not submit on the keystroke that picks an option.
          event.preventDefault();
          const option = showing[cursor];
          if (option) choose(option);
        }
        break;
      case 'Escape':
        if (open) {
          // Ours, not the dialog's: closing the list is the first Escape, the
          // dialog's is the next.
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          onQueryChange('');
        }
        break;
      case 'Backspace':
        if (!open && value !== null && query === '') {
          // Backspace on a chosen value clears it, as a tag input does.
          event.preventDefault();
          onValueChange(null);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className={cx(styles.root, className)} data-combobox-open={open}>
      <div className={cx(styles.control, field.invalid && styles.controlInvalid)}>
        <input
          ref={inputRef}
          id={controlId}
          className={styles.input}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-invalid={field.invalid || undefined}
          aria-describedby={field.describedBy}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          {...(activeId ? { 'aria-activedescendant': activeId } : {})}
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          placeholder={placeholder}
          value={shown}
          onChange={(event) => {
            onQueryChange(event.target.value);
            setActiveIndex(0);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onClick={() => {
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        {value !== null && !disabled ? (
          <button
            type="button"
            className={styles.adornmentButton}
            aria-label="Clear the selection"
            onClick={() => {
              onValueChange(null);
              onQueryChange('');
              inputRef.current?.focus();
            }}
          >
            <Close size={14} />
          </button>
        ) : (
          <span className={styles.adornment} aria-hidden="true">
            <ChevronUpDown size={14} />
          </span>
        )}
      </div>
      <div
        id={listId}
        ref={listRef}
        role="listbox"
        className={cx(styles.list, !open && styles.listHidden)}
        aria-label={ariaLabel ? `${ariaLabel} options` : undefined}
      >
        {status ? (
          <div className={styles.status}>{status}</div>
        ) : (
          showing.map((option, index) => (
            <button
              key={option.value}
              id={`${listId}-${index}`}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={option.value === value}
              className={cx(styles.item, index === cursor && styles.itemActive)}
              onMouseDown={(event) => {
                // Keep focus in the input (see CommandPalette).
                event.preventDefault();
              }}
              onMouseMove={() => {
                setActiveIndex(index);
              }}
              onClick={() => {
                choose(option);
              }}
            >
              <span className={styles.itemLabel}>{option.label ?? option.value}</span>
              {option.trailing ? (
                <span className={styles.itemTrailing}>{option.trailing}</span>
              ) : null}
            </button>
          ))
        )}
        {!status && footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>
  );
}
